import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";
import {
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Paper,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
  makeStyles,
} from "@material-ui/core";
import { FileCopy, Send, DeleteOutline } from "@material-ui/icons";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import ConfirmationModal from "../ConfirmationModal";
import { AuthContext } from "../../context/Auth/AuthContext";
import { usePrivacyCensorSettings } from "../../utils/privacyCensor";

const useStyles = makeStyles(theme => ({
  headerActions: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1),
    flexWrap: "wrap",
  },
  dialogTitle: {
    paddingBottom: theme.spacing(1),
  },
  tabs: {
    minHeight: 38,
    marginBottom: theme.spacing(1),
    "& .MuiTab-root": {
      minHeight: 38,
      textTransform: "none",
    },
  },
  filterChips: {
    display: "flex",
    gap: theme.spacing(0.75),
    flexWrap: "wrap",
    marginBottom: theme.spacing(1.5),
  },
  quickDateButtons: {
    display: "flex",
    gap: theme.spacing(1),
    flexWrap: "wrap",
    alignItems: "center",
  },
  row: {
    padding: theme.spacing(1.25),
    marginBottom: theme.spacing(1),
    border: "1px solid rgba(0,0,0,.12)",
    borderRadius: 6,
    display: "grid",
    gridTemplateColumns: "116px repeat(4, minmax(130px, 1fr)) 104px auto",
    gap: theme.spacing(1),
    alignItems: "center",
    overflowX: "auto",
    [theme.breakpoints.down("sm")]: {
      gridTemplateColumns: "1fr",
    },
  },
  rowActions: {
    display: "flex",
    justifyContent: "flex-end",
    alignItems: "center",
    gap: theme.spacing(0.5),
    [theme.breakpoints.down("sm")]: {
      justifyContent: "flex-start",
    },
  },
  availableGrid: {
    display: "grid",
    gap: theme.spacing(1),
  },
  rowHeading: {
    minWidth: 0,
  },
  rowPlatform: {
    lineHeight: 1.2,
  },
  rowChip: {
    justifySelf: "stretch",
    minWidth: 96,
  },
  clickableChip: {
    cursor: "pointer",
  },
  countdownSummary: {
    display: "grid",
    gap: theme.spacing(1),
    marginTop: theme.spacing(0.5),
  },
  countdownCard: {
    padding: theme.spacing(1),
    border: "1px solid rgba(0,0,0,.1)",
  },
  countdownCardHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: theme.spacing(1),
  },
  countdownCardActions: {
    display: "flex",
    alignItems: "center",
    flexShrink: 0,
  },
  emptyText: {
    color: theme.palette.text.secondary,
  },
  censoredText: {
    filter: "blur(4px)",
    userSelect: "none",
  },
}));

const activeStatuses = ["active", "assigned"];

const pad = value => String(value).padStart(2, "0");

const toDateTimeInput = value => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(
    date.getHours()
  )}:${pad(date.getMinutes())}`;
};

const nowDateTimeInput = () => {
  const date = new Date();
  date.setSeconds(0, 0);
  return toDateTimeInput(date);
};

const addDaysToDateTimeInput = (value, days) => {
  const date = value ? new Date(value) : new Date();
  if (Number.isNaN(date.getTime())) return "";
  date.setDate(date.getDate() + days);
  date.setSeconds(0, 0);
  return toDateTimeInput(date);
};

const formatDate = value => (value ? new Date(value).toLocaleDateString("es-CR") : "-");

const streamingReminderStatusLabels = {
  pending: "Pendiente",
  processing: "Procesando",
  sent: "Enviado",
  failed: "Fallido",
};

const formatStreamingReminderStatus = status =>
  streamingReminderStatusLabels[status] || status || "";

const formatDateTime = (value, timeZone = "") => {
  if (!value) return "-";
  const options = {
    year: "numeric",
    month: "numeric",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
    ...(timeZone ? { timeZone } : {}),
  };

  try {
    return new Date(value).toLocaleString("es-CR", options);
  } catch {
    return new Date(value).toLocaleString("es-CR", {
      year: "numeric",
      month: "numeric",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
  }
};

const getActiveAssignment = assignments =>
  (assignments || []).find(item => activeStatuses.includes(item.status)) || null;

const normalizeFetchedAccounts = accounts =>
  (accounts || []).map(account => ({
    ...account,
    assignments: [...(account.assignments || [])].sort((a, b) => Number(b.id || 0) - Number(a.id || 0)),
    profiles: [...(account.profiles || [])]
      .sort((a, b) => Number(a.id || 0) - Number(b.id || 0))
      .map(profile => ({
        ...profile,
        assignments: [...(profile.assignments || [])].sort((a, b) => Number(b.id || 0) - Number(a.id || 0)),
      })),
  }));

const normalizeAccountDraft = account => ({
  email: account?.email || "",
  password: account?.password || "",
  profiles: (account?.profiles || []).map(profile => ({
    id: profile.id,
    name: profile.name || "",
    pin: profile.pin || "",
  })),
});

const normalizeAssignmentDraft = assignment => ({
  startsAt: toDateTimeInput(assignment?.startsAt || new Date()),
  endsAt: toDateTimeInput(assignment?.endsAt),
});

const getDaysLeft = assignment => {
  if (!assignment?.endsAt) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const endDate = new Date(assignment.endsAt);
  if (Number.isNaN(endDate.getTime())) return null;
  endDate.setHours(0, 0, 0, 0);
  return Math.ceil((endDate.getTime() - today.getTime()) / 86400000);
};

const getAssignmentChipProps = assignment => {
  if (!assignment) return { label: "Disponible", style: { backgroundColor: "#fdab3d", color: "#fff" } };

  const daysLeft = getDaysLeft(assignment);
  if (daysLeft === null) return { label: "Sin vencimiento", color: "primary" };
  if (daysLeft < 0) return { label: `${daysLeft} dias`, style: { backgroundColor: "#9f44dc", color: "#fff" } };
  if (daysLeft === 0) return { label: "0 dias", style: { backgroundColor: "#ea4335", color: "#fff" } };
  if (daysLeft === 1) return { label: "1 dia", style: { backgroundColor: "#4285f4", color: "#fff" } };
  return { label: `${daysLeft} dias`, color: "primary" };
};

const buildAccessSummary = (platform, account, profile, assignment) => {
  const daysLeft = getDaysLeft(assignment);
  return [
    `*${platform?.name || "Streaming"}*`,
    "",
    account?.email ? `Correo: ${account.email}` : "",
    account?.password ? `Contrasena: ${account.password}` : "",
    profile?.name ? `Perfil: ${profile.name}` : "Cuenta completa",
    profile?.pin ? `PIN: ${profile.pin}` : "",
    assignment?.startsAt ? `Inicia: ${formatDate(assignment.startsAt)}` : "",
    assignment?.endsAt ? `Finaliza: ${formatDate(assignment.endsAt)}` : "",
    assignment?.endsAt && daysLeft !== null ? `Quedan: ${daysLeft} dias` : "",
  ].filter(Boolean).join("\n");
};

const TicketStreamingModal = ({ modalOpen, onClose, ticket }) => {
  const classes = useStyles();
  const { user } = useContext(AuthContext);
  const privacyCensorSettings = usePrivacyCensorSettings(user?.id);
  const isMounted = useRef(true);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [platforms, setPlatforms] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [accountDrafts, setAccountDrafts] = useState({});
  const [assignmentDrafts, setAssignmentDrafts] = useState({});
  const [confirmSaveRow, setConfirmSaveRow] = useState(null);
  const [confirmAssignment, setConfirmAssignment] = useState(null);
  const [dateAssignment, setDateAssignment] = useState(null);
  const [countdownAssignment, setCountdownAssignment] = useState(null);
  const [deleteCountdown, setDeleteCountdown] = useState(null);
  const [releaseTarget, setReleaseTarget] = useState(null);
  const [assignTarget, setAssignTarget] = useState(null);
  const [mainTab, setMainTab] = useState("assigned");
  const [assignedFilter, setAssignedFilter] = useState("all");
  const [availableFilter, setAvailableFilter] = useState("all");

  const contactId = ticket?.contactId || ticket?.contact?.id;

  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  const platformById = useMemo(() => {
    const map = {};
    platforms.forEach(platform => {
      map[platform.id] = platform;
    });
    return map;
  }, [platforms]);

  const replaceAccount = updatedAccount => {
    if (!updatedAccount?.id) return;
    const [normalizedAccount] = normalizeFetchedAccounts([updatedAccount]);
    setAccounts(prev =>
      prev.map(account => (Number(account.id) === Number(updatedAccount.id) ? normalizedAccount : account))
    );
    setAccountDrafts(prev => ({
      ...prev,
      [normalizedAccount.id]: normalizeAccountDraft(normalizedAccount),
    }));
  };

  const fetchData = async () => {
    if (!modalOpen || !ticket?.id) return;

    setLoading(true);
    try {
      const [{ data: platformData }, { data: accountData }] = await Promise.all([
        api.get("/streaming/platforms"),
        api.get("/streaming/accounts"),
      ]);

      if (isMounted.current) {
        const normalizedAccounts = normalizeFetchedAccounts(accountData.accounts);
        setPlatforms(platformData.platforms || []);
        setAccounts(normalizedAccounts);
        setAccountDrafts(
          normalizedAccounts.reduce((acc, account) => {
            acc[account.id] = normalizeAccountDraft(account);
            return acc;
          }, {})
        );
      }
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (modalOpen) {
      setMainTab("assigned");
      setAssignedFilter("all");
      setAvailableFilter("all");
      fetchData();
    }
  }, [modalOpen, ticket?.id]);

  useEffect(() => {
    setAssignmentDrafts(prev => {
      const next = { ...prev };
      accounts.forEach(account => {
        const accountAssignment = getActiveAssignment(account.assignments);
        if (accountAssignment) {
          const key = `account-${account.id}`;
          if (!next[key]) next[key] = normalizeAssignmentDraft(accountAssignment);
        }
        (account.profiles || []).forEach(profile => {
          const profileAssignment = getActiveAssignment(profile.assignments);
          if (profileAssignment) {
            const key = `profile-${profile.id}`;
            if (!next[key]) next[key] = normalizeAssignmentDraft(profileAssignment);
          }
        });
      });
      return next;
    });
  }, [accounts]);

  const currentAssignments = useMemo(() => {
    const rows = [];
    accounts.forEach(account => {
      const platform = platformById[account.platformId];
      const accountAssignment = getActiveAssignment(account.assignments);
      const accountAssignmentContactId = accountAssignment?.contactId || accountAssignment?.contact?.id;
      if (accountAssignment && String(accountAssignmentContactId) === String(contactId)) {
        rows.push({ type: "account", id: account.id, account, platform, profile: null, assignment: accountAssignment });
      }
      (account.profiles || []).forEach(profile => {
        const profileAssignment = getActiveAssignment(profile.assignments);
        const profileAssignmentContactId = profileAssignment?.contactId || profileAssignment?.contact?.id;
        if (profileAssignment && String(profileAssignmentContactId) === String(contactId)) {
          rows.push({ type: "profile", id: profile.id, account, platform, profile, assignment: profileAssignment });
        }
      });
    });
    return rows;
  }, [accounts, contactId, platformById]);

  const availableItems = useMemo(() => {
    const rows = [];
    accounts.forEach(account => {
      const platform = platformById[account.platformId];
      const accountAssignment = getActiveAssignment(account.assignments);
      const accountAssignmentContactId = accountAssignment?.contactId || accountAssignment?.contact?.id;
      if (!accountAssignment || !accountAssignmentContactId) {
        rows.push({
          type: "account",
          id: account.id,
          account,
          platform,
          profile: null,
          assignment: accountAssignment || null,
        });
      }
      (account.profiles || []).forEach(profile => {
        const profileAssignment = getActiveAssignment(profile.assignments);
        const profileAssignmentContactId = profileAssignment?.contactId || profileAssignment?.contact?.id;
        if (!profileAssignment || !profileAssignmentContactId) {
          rows.push({
            type: "profile",
            id: profile.id,
            account,
            platform,
            profile,
            assignment: profileAssignment || null,
          });
        }
      });
    });
    return rows;
  }, [accounts, platformById]);

  const assignedPlatformFilters = useMemo(() => {
    const seen = new Set();
    return currentAssignments
      .map(row => row.platform)
      .filter(platform => {
        if (!platform?.id || seen.has(platform.id)) return false;
        seen.add(platform.id);
        return true;
      });
  }, [currentAssignments]);

  const availablePlatformFilters = useMemo(() => {
    const seen = new Set();
    return availableItems
      .map(row => row.platform)
      .filter(platform => {
        if (!platform?.id || seen.has(platform.id)) return false;
        seen.add(platform.id);
        return true;
      });
  }, [availableItems]);

  const filteredCurrentAssignments = useMemo(() => {
    return currentAssignments.filter(row => {
      if (assignedFilter === "all") return true;
      if (assignedFilter === "active") return getDaysLeft(row.assignment) >= 0;
      if (assignedFilter === "expired") return getDaysLeft(row.assignment) < 0;
      if (assignedFilter === "profiles") return row.type === "profile";
      if (assignedFilter === "accounts") return row.type === "account";
      if (assignedFilter.startsWith("platform-")) {
        return String(row.platform?.id) === assignedFilter.replace("platform-", "");
      }
      return true;
    });
  }, [assignedFilter, currentAssignments]);

  const filteredAvailableItems = useMemo(() => {
    return availableItems.filter(row => {
      if (availableFilter === "all") return true;
      if (availableFilter === "profiles") return row.type === "profile";
      if (availableFilter === "accounts") return row.type === "account";
      if (availableFilter.startsWith("platform-")) {
        return String(row.platform?.id) === availableFilter.replace("platform-", "");
      }
      return true;
    });
  }, [availableFilter, availableItems]);

  const getAccountDraft = account => accountDrafts[account.id] || normalizeAccountDraft(account);

  const getProfileDraft = (account, profile) =>
    getAccountDraft(account).profiles.find(item => Number(item.id) === Number(profile?.id)) || {
      id: profile?.id,
      name: profile?.name || "",
      pin: profile?.pin || "",
    };

  const updateAccountDraft = (account, patch) => {
    setAccountDrafts(prev => ({
      ...prev,
      [account.id]: {
        ...getAccountDraft(account),
        ...patch,
      },
    }));
  };

  const updateProfileDraft = (account, profile, patch) => {
    setAccountDrafts(prev => {
      const draft = prev[account.id] || normalizeAccountDraft(account);
      return {
        ...prev,
        [account.id]: {
          ...draft,
          profiles: draft.profiles.map(item =>
            Number(item.id) === Number(profile.id) ? { ...item, ...patch } : item
          ),
        },
      };
    });
  };

  const saveAccountDraft = async account => {
    const draft = getAccountDraft(account);
    setSaving(true);
    try {
      const { data } = await api.put(`/streaming/accounts/${account.id}`, {
        email: draft.email,
        password: draft.password,
        profiles: draft.profiles,
      });
      replaceAccount(data);
      toast.success("Cuenta guardada.");
    } catch (err) {
      toastError(err);
    } finally {
      setSaving(false);
    }
  };

  const getAssignmentDraft = row => {
    const key = `${row.type}-${row.id}`;
    return assignmentDrafts[key] || normalizeAssignmentDraft(row.assignment);
  };

  const updateAssignmentDraft = (row, patch) => {
    const key = `${row.type}-${row.id}`;
    setAssignmentDrafts(prev => ({
      ...prev,
      [key]: {
        ...getAssignmentDraft(row),
        ...patch,
      },
    }));
  };

  const requestAccountSave = row => {
    if (!row?.account?.id) return;
    const draft = getAccountDraft(row.account);
    const original = normalizeAccountDraft(row.account);
    if (JSON.stringify(draft) !== JSON.stringify(original)) setConfirmSaveRow(row);
  };

  const requestAssignmentSave = (row, draft = null) => {
    if (row?.id) setConfirmAssignment({ row, draft });
  };

  const requestDateAssignmentSave = (row, editableAssignment) => {
    if (!row?.id) return;
    setDateAssignment({
      row,
      editableAssignment,
      draft: { ...getAssignmentDraft(row) },
    });
  };

  const saveAssignmentDraft = async (row, draftOverride = null) => {
    const draft = draftOverride || getAssignmentDraft(row);
    if (!contactId) {
      toast.error("Este ticket no tiene contacto.");
      return;
    }
    if (!draft.endsAt) {
      toast.error("Selecciona fecha final.");
      return;
    }

    setSaving(true);
    try {
      const endpoint =
        row.type === "account"
          ? `/streaming/accounts/${row.account.id}/assign`
          : `/streaming/profiles/${row.profile.id}/assign`;
      const { data } = await api.post(endpoint, {
        contactId,
        startsAt: draft.startsAt || nowDateTimeInput(),
        endsAt: draft.endsAt,
        connectionId: ticket.connectionId || row.platform?.connectionId || "",
      });
      setAssignmentDrafts(prev => {
        const next = { ...prev };
        delete next[`${row.type}-${row.id}`];
        return next;
      });
      replaceAccount(data);
      toast.success("Asignacion guardada.");
    } catch (err) {
      toastError(err);
    } finally {
      setSaving(false);
    }
  };

  const assignItem = async item => {
    if (!contactId) {
      toast.error("Este ticket no tiene contacto.");
      return;
    }
    const draft = getAssignmentDraft(item);
    if (!draft.endsAt) {
      toast.error("Selecciona fecha final.");
      return;
    }

    setSaving(true);
    try {
      const endpoint =
        item.type === "account"
          ? `/streaming/accounts/${item.account.id}/assign`
          : `/streaming/profiles/${item.profile.id}/assign`;
      const { data } = await api.post(endpoint, {
        contactId,
        startsAt: draft.startsAt || nowDateTimeInput(),
        endsAt: draft.endsAt,
        connectionId: ticket.connectionId || item.platform?.connectionId || "",
      });
      toast.success("Streaming asignado.");
      replaceAccount(data);
    } catch (err) {
      toastError(err);
    } finally {
      setSaving(false);
    }
  };

  const requestAssignItem = item => {
    if (!contactId) {
      toast.error("Este ticket no tiene contacto.");
      return;
    }
    const draft = getAssignmentDraft(item);
    if (!draft.endsAt) {
      toast.error("Selecciona fecha final.");
      return;
    }
    setAssignTarget(item);
  };

  const releaseItem = async target => {
    try {
      const endpoint =
        target.type === "account"
          ? `/streaming/accounts/${target.account.id}/release`
          : `/streaming/profiles/${target.profile.id}/release`;
      const { data } = await api.post(endpoint);
      toast.success("Streaming liberado.");
      setReleaseTarget(null);
      setAssignmentDrafts(prev => {
        const next = { ...prev };
        delete next[`${target.type}-${target.id}`];
        return next;
      });
      replaceAccount(data);
    } catch (err) {
      toastError(err);
    }
  };

  const removeCountdownReminder = async reminder => {
    if (!reminder?.id) return;

    try {
      await api.delete(`/streaming/countdowns/${reminder.id}`);
      setCountdownAssignment(prev =>
        prev
          ? {
              ...prev,
              reminders: (prev.reminders || []).filter(item => item.id !== reminder.id),
            }
          : prev
      );
      setAccounts(prevAccounts =>
        prevAccounts.map(account => ({
          ...account,
          assignments: (account.assignments || []).map(assignment =>
            Number(assignment.id) === Number(countdownAssignment?.id)
              ? {
                  ...assignment,
                  reminders: (assignment.reminders || []).filter(item => item.id !== reminder.id),
                }
              : assignment
          ),
          profiles: (account.profiles || []).map(profile => ({
            ...profile,
            assignments: (profile.assignments || []).map(assignment =>
              Number(assignment.id) === Number(countdownAssignment?.id)
                ? {
                    ...assignment,
                    reminders: (assignment.reminders || []).filter(item => item.id !== reminder.id),
                  }
                : assignment
            ),
          })),
        }))
      );
      toast.success("Countdown eliminado.");
    } catch (err) {
      toastError(err);
    } finally {
      setDeleteCountdown(null);
    }
  };

  const copyAccess = async row => {
    try {
      await navigator.clipboard.writeText(
        buildAccessSummary(row.platform, row.account, row.profile, row.assignment)
      );
      toast.success("Datos copiados.");
    } catch {
      toast.error("No se pudo copiar al portapapeles.");
    }
  };

  const sendAssignment = async assignment => {
    try {
      await api.post(`/streaming/assignments/${assignment.id}/send`);
      toast.success("Datos enviados al chat.");
    } catch (err) {
      toastError(err);
    }
  };

  const renderFilterChips = (value, onChange, platformFilters, includeStatus = false) => (
    <div className={classes.filterChips}>
      <Chip
        size="small"
        label="Todas"
        clickable
        color={value === "all" ? "primary" : "default"}
        onClick={() => onChange("all")}
      />
      {includeStatus && (
        <>
          <Chip
            size="small"
            label="Activas"
            clickable
            color={value === "active" ? "primary" : "default"}
            onClick={() => onChange("active")}
          />
          <Chip
            size="small"
            label="Vencidas"
            clickable
            color={value === "expired" ? "primary" : "default"}
            onClick={() => onChange("expired")}
          />
        </>
      )}
      <Chip
        size="small"
        label="Perfiles"
        clickable
        color={value === "profiles" ? "primary" : "default"}
        onClick={() => onChange("profiles")}
      />
      <Chip
        size="small"
        label="Cuentas completas"
        clickable
        color={value === "accounts" ? "primary" : "default"}
        onClick={() => onChange("accounts")}
      />
      {platformFilters.map(platform => {
        const filterValue = `platform-${platform.id}`;
        return (
          <Chip
            key={filterValue}
            size="small"
            label={platform.name}
            clickable
            color={value === filterValue ? "primary" : "default"}
            onClick={() => onChange(filterValue)}
          />
        );
      })}
    </div>
  );

  const renderEditableFields = (row, editableAssignment = false) => {
    const accountDraft = getAccountDraft(row.account);
    const profileDraft = row.profile ? getProfileDraft(row.account, row.profile) : null;
    const assignmentDraft = getAssignmentDraft(row);

    return (
      <>
        <div className={classes.rowHeading}>
          <Typography variant="subtitle2" noWrap className={classes.rowPlatform}>
            {row.platform?.name || "Streaming"}
          </Typography>
          <Typography variant="caption" color="textSecondary">
            {row.profile ? "Perfil" : "Cuenta completa"}
          </Typography>
        </div>
        {row.profile ? (
          <>
            <TextField
              label="Perfil"
              variant="outlined"
              margin="dense"
              value={profileDraft?.name || ""}
              onChange={event => updateProfileDraft(row.account, row.profile, { name: event.target.value })}
              onBlur={() => requestAccountSave(row)}
            />
            <TextField
              label="PIN"
              variant="outlined"
              margin="dense"
              value={profileDraft?.pin || ""}
              onChange={event => updateProfileDraft(row.account, row.profile, { pin: event.target.value })}
              onBlur={() => requestAccountSave(row)}
            />
          </>
        ) : (
          <>
            <TextField
              label="Correo"
              variant="outlined"
              margin="dense"
              value={accountDraft.email}
              onChange={event => updateAccountDraft(row.account, { email: event.target.value })}
              onBlur={() => requestAccountSave(row)}
            />
            <TextField
              label="Contrasena"
              variant="outlined"
              margin="dense"
              value={accountDraft.password}
              onChange={event => updateAccountDraft(row.account, { password: event.target.value })}
              onBlur={() => requestAccountSave(row)}
            />
          </>
        )}
        <TextField
          label="Inicio"
          type="datetime-local"
          variant="outlined"
          margin="dense"
          InputLabelProps={{ shrink: true }}
          value={assignmentDraft.startsAt || ""}
          onMouseDown={event => {
            event.preventDefault();
            requestDateAssignmentSave(row, editableAssignment);
          }}
          onChange={event => updateAssignmentDraft(row, { startsAt: event.target.value })}
        />
        <TextField
          label="Finaliza"
          type="datetime-local"
          variant="outlined"
          margin="dense"
          InputLabelProps={{ shrink: true }}
          value={assignmentDraft.endsAt || ""}
          onChange={event => {
            const nextDraft = { ...assignmentDraft, endsAt: event.target.value };
            updateAssignmentDraft(row, { endsAt: event.target.value });
            if (editableAssignment) requestAssignmentSave(row, nextDraft);
          }}
        />
      </>
    );
  };

  return (
    <>
      <Dialog open={modalOpen} onClose={onClose} maxWidth="md" fullWidth scroll="paper">
        <DialogTitle className={classes.dialogTitle}>Streaming</DialogTitle>
        <DialogContent dividers>
          <div className={classes.headerActions}>
            <Typography
              variant="body2"
              color="textSecondary"
              className={privacyCensorSettings.contactNames ? classes.censoredText : undefined}
            >
              {privacyCensorSettings.contactNames
                ? "Contacto privado"
                : `${ticket?.contact?.name || ""} ${
                    ticket?.contact?.number ? `- ${ticket.contact.number}` : ""
                  }`}
            </Typography>
          </div>

          <Tabs
            value={mainTab}
            onChange={(_, value) => setMainTab(value)}
            indicatorColor="primary"
            textColor="primary"
            className={classes.tabs}
          >
            <Tab value="assigned" label="Asociados a este cliente" />
            <Tab value="available" label="Disponibles para asignar" />
          </Tabs>

          {mainTab === "assigned" && (
            <>
              {renderFilterChips(assignedFilter, setAssignedFilter, assignedPlatformFilters, true)}
              {loading ? (
                <CircularProgress size={24} />
              ) : filteredCurrentAssignments.length ? (
                filteredCurrentAssignments.map(row => {
                  const chipProps = getAssignmentChipProps(row.assignment);
                  return (
                    <Paper className={classes.row} elevation={0} key={`${row.type}-${row.assignment.id}`}>
                      {renderEditableFields(row, true)}
                      <Chip
                        size="small"
                        {...chipProps}
                        className={`${classes.rowChip} ${row.assignment ? classes.clickableChip : ""}`}
                        onClick={row.assignment ? () => setCountdownAssignment(row.assignment) : undefined}
                      />
                      <div className={classes.rowActions}>
                        <Tooltip title="Copiar datos">
                          <IconButton size="small" onClick={() => copyAccess(row)}>
                            <FileCopy fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Enviar al chat">
                          <IconButton size="small" onClick={() => sendAssignment(row.assignment)}>
                            <Send fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Liberar">
                          <IconButton size="small" onClick={() => setReleaseTarget(row)}>
                            <DeleteOutline fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </div>
                    </Paper>
                  );
                })
              ) : (
                <Typography variant="body2" className={classes.emptyText}>
                  Este filtro no tiene cuentas o perfiles asociados.
                </Typography>
              )}
            </>
          )}

          {mainTab === "available" && (
            <>
              {renderFilterChips(availableFilter, setAvailableFilter, availablePlatformFilters)}
              {loading ? (
                <CircularProgress size={24} />
              ) : filteredAvailableItems.length ? (
                <div className={classes.availableGrid}>
                  {filteredAvailableItems.map(row => (
                    <Paper className={classes.row} elevation={0} key={`${row.type}-${row.id}`}>
                      {renderEditableFields(row, false)}
                      <Chip size="small" className={classes.rowChip} {...getAssignmentChipProps(row.assignment)} />
                      <div className={classes.rowActions}>
                        <Tooltip title="Copiar datos">
                          <IconButton
                            size="small"
                            onClick={() => copyAccess({ ...row, assignment: getAssignmentDraft(row) })}
                          >
                            <FileCopy fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Button
                          color="primary"
                          variant="contained"
                          size="small"
                          disabled={saving}
                          onClick={() => requestAssignItem(row)}
                        >
                          Asignar
                        </Button>
                      </div>
                    </Paper>
                  ))}
                </div>
              ) : (
                <Typography variant="body2" className={classes.emptyText}>
                  Este filtro no tiene cuentas o perfiles disponibles.
                </Typography>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} color="secondary" variant="outlined">
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(countdownAssignment)} onClose={() => setCountdownAssignment(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Countdowns programados</DialogTitle>
        <DialogContent dividers>
          <div className={classes.countdownSummary}>
            {(countdownAssignment?.reminders || []).length ? (
              (countdownAssignment.reminders || []).map((reminder, index) => (
                <Paper key={reminder.id || index} className={classes.countdownCard} elevation={0}>
                  <div className={classes.countdownCardHeader}>
                    <div>
                      <Typography variant="subtitle2">Countdown {index + 1}</Typography>
                      <Typography variant="body2" color="textSecondary">
                        {formatDateTime(reminder.scheduledAt, countdownAssignment?.connection?.timeZone)} - {reminder.chatbotSequence?.name || `Secuencia #${reminder.chatbotSequenceId || "-"}`} - {formatStreamingReminderStatus(reminder.status)}
                      </Typography>
                      {reminder.lastError && (
                        <Typography variant="caption" color="error">
                          {reminder.lastError}
                        </Typography>
                      )}
                    </div>
                    <div className={classes.countdownCardActions}>
                      <IconButton
                        size="small"
                        onClick={() => setDeleteCountdown(reminder)}
                      >
                        <DeleteOutline fontSize="small" />
                      </IconButton>
                    </div>
                  </div>
                </Paper>
              ))
            ) : (
              <Typography variant="body2" color="textSecondary">
                Esta asignacion no tiene countdowns programados.
              </Typography>
            )}
          </div>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCountdownAssignment(null)}>Cerrar</Button>
        </DialogActions>
      </Dialog>

      <ConfirmationModal
        title="Guardar cambio"
        open={!!confirmSaveRow}
        onClose={() => setConfirmSaveRow(null)}
        onConfirm={async () => {
          if (confirmSaveRow?.account) {
            await saveAccountDraft(confirmSaveRow.account);
          }
          setConfirmSaveRow(null);
        }}
      >
        Deseas guardar este cambio?
      </ConfirmationModal>

      <ConfirmationModal
        title="Guardar cambio"
        open={!!confirmAssignment}
        onClose={() => setConfirmAssignment(null)}
        onConfirm={async () => {
          if (confirmAssignment?.row) {
            await saveAssignmentDraft(confirmAssignment.row, confirmAssignment.draft);
          }
          setConfirmAssignment(null);
        }}
      >
        Deseas guardar este cambio?
      </ConfirmationModal>

      <Dialog open={!!dateAssignment} onClose={() => setDateAssignment(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Ajustar fechas</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                label="Inicio"
                type="datetime-local"
                variant="outlined"
                margin="dense"
                fullWidth
                InputLabelProps={{ shrink: true }}
                value={dateAssignment?.draft?.startsAt || ""}
                onChange={event =>
                  setDateAssignment(prev =>
                    prev ? { ...prev, draft: { ...prev.draft, startsAt: event.target.value } } : prev
                  )
                }
              />
            </Grid>
            <Grid item xs={12}>
              <div className={classes.quickDateButtons}>
                {[30, 60, 90].map(days => (
                  <Button
                    key={days}
                    size="small"
                    variant="outlined"
                    onClick={() =>
                      setDateAssignment(prev =>
                        prev
                          ? {
                              ...prev,
                              draft: {
                                ...prev.draft,
                                startsAt: prev.draft.startsAt || nowDateTimeInput(),
                                endsAt: addDaysToDateTimeInput(prev.draft.startsAt || nowDateTimeInput(), days),
                              },
                            }
                          : prev
                      )
                    }
                  >
                    {days} dias
                  </Button>
                ))}
              </div>
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Finaliza"
                type="datetime-local"
                variant="outlined"
                margin="dense"
                fullWidth
                InputLabelProps={{ shrink: true }}
                value={dateAssignment?.draft?.endsAt || ""}
                onChange={event =>
                  setDateAssignment(prev =>
                    prev ? { ...prev, draft: { ...prev.draft, endsAt: event.target.value } } : prev
                  )
                }
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDateAssignment(null)}>Cancelar</Button>
          <Button
            color="primary"
            variant="contained"
            onClick={async () => {
              if (dateAssignment?.row) {
                if (dateAssignment.editableAssignment) {
                  await saveAssignmentDraft(dateAssignment.row, dateAssignment.draft);
                } else {
                  updateAssignmentDraft(dateAssignment.row, dateAssignment.draft);
                }
              }
              setDateAssignment(null);
            }}
          >
            Guardar
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmationModal
        title="Asignar streaming"
        open={!!assignTarget}
        onClose={() => setAssignTarget(null)}
        onConfirm={async () => {
          if (assignTarget) {
            await assignItem(assignTarget);
          }
          setAssignTarget(null);
        }}
      >
        Deseas asignar este streaming al contacto del chat?
      </ConfirmationModal>

      <ConfirmationModal
        title="Liberar streaming"
        open={!!releaseTarget}
        onClose={() => setReleaseTarget(null)}
        onConfirm={() => releaseItem(releaseTarget)}
      >
        Se quitara esta asignacion y sus countdowns pendientes.
      </ConfirmationModal>

      <ConfirmationModal
        title="Eliminar countdown"
        open={!!deleteCountdown}
        onClose={() => setDeleteCountdown(null)}
        onConfirm={() => removeCountdownReminder(deleteCountdown)}
      >
        Deseas eliminar este countdown?
      </ConfirmationModal>
    </>
  );
};

export default TicketStreamingModal;
